/**
 * fetch wrapper that attaches the Fabric ID token.
 *
 * Copied into every module. The only per-module difference is the base URL.
 */
import { getIdToken } from "./auth";

const BASE = import.meta.env.VITE_FABRIC_API_URL ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * The API's machine-readable discriminator. Only `step_up_required` matters
     * today: the client must re-authenticate and retry rather than showing the
     * message, which is the difference between "confirm it is you" and a
     * dead-end error banner on the one person allowed to do this.
     */
    readonly code?: string,
    /** Extra fields the server sent alongside `code` (e.g. `expected`). */
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
  }

  get needsStepUp(): boolean {
    return this.code === "step_up_required";
  }

  /**
   * The action targets a project Fabric does not deploy, and the server wants the
   * service name typed back before it will act. Distinct from step-up: that asks
   * *are you still you*, this asks *did you mean this row*.
   */
  get needsConfirmation(): boolean {
    return this.code === "confirmation_required";
  }

  /** The exact phrase the server will accept. Never parsed out of the message. */
  get expectedPhrase(): string | null {
    const e = this.detail?.expected;
    return typeof e === "string" ? e : null;
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new ApiError(401, "Not signed in");

  // An absolute URL passes through untouched. The Front Door's search box calls
  // Vault's endpoint directly, and blindly prefixing BASE would build
  // "https://fabric-api…https://vault-api…/search". Both services verify the
  // same Fabric-issued token, so the header is correct either way.
  const url = /^https?:\/\//.test(path) ? path : `${BASE}${path}`;

  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    // The API answers errors as {error}. Fall back to the status text when a
    // proxy or a cold start returns HTML instead, so the UI never shows
    // "Unexpected token < in JSON".
    const body = await res
      .json()
      .then((j: { error?: string; code?: string }) => j)
      .catch(() => null);
    throw new ApiError(res.status, body?.error ?? res.statusText, body?.code, body ?? undefined);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  get: <T>(path: string) => call<T>("GET", path),
  post: <T>(path: string, body?: unknown) => call<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => call<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => call<T>("PATCH", path, body ?? {}),
  del: <T>(path: string) => call<T>("DELETE", path),
};
